import { useEffect, useMemo, useState } from 'react';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { useModalClose } from '../hooks/useModalClose';
import {
  EXPORT_COLUMNS,
  getColumnById,
  loadExportConfig,
  saveExportConfig,
} from '../utils/exportColumns';

/**
 * Modal that lets the user pick the CSV header language and choose / reorder
 * columns before exporting sales data.
 *
 * Props:
 *  - isOpen, onClose
 *  - onConfirm({ language, selectedColumns }) — called when the user clicks Export
 *  - theme — theme object (passed in by parent, both callers already have one)
 *  - callerKey — string used to namespace the localStorage slot
 *  - defaultLanguage — initial language ('en' | 'es')
 *  - defaultSelectedColumns — preset column ids in order (used on first open and Reset)
 *  - exporting — when true, disables the Export button and shows the in-flight label
 */
export default function ExportConfigModal({
  isOpen,
  onClose,
  onConfirm,
  theme,
  callerKey,
  defaultLanguage,
  defaultSelectedColumns,
  exporting = false,
}) {
  const { t, translateInLanguage } = useLanguage();
  useModalClose(onClose, isOpen);

  const fallback = useMemo(
    () => ({ language: defaultLanguage, selectedColumns: defaultSelectedColumns }),
    [defaultLanguage, defaultSelectedColumns]
  );

  const [language, setLanguage] = useState(defaultLanguage);
  const [selectedIds, setSelectedIds] = useState(defaultSelectedColumns);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  // On open: pre-paint instantly from localStorage cache, then reconcile against
  // the server (per-domain source of truth). The server value wins if it differs,
  // so two users on the same tenant — even in different browsers — see the same
  // preset. localStorage is just a flicker-free fallback for slow networks.
  useEffect(() => {
    if (!isOpen) return;
    const cached = loadExportConfig(callerKey, fallback);
    setLanguage(cached.language);
    setSelectedIds(cached.selectedColumns);

    let cancelled = false;
    fetch(`${BACKEND_URL}/api/export-config/${callerKey}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (cancelled || !body || !body.config) return;
        const validIds = (body.config.selectedColumns || []).filter((id) => getColumnById(id));
        if (validIds.length === 0) return;
        const language = body.config.language === 'es' ? 'es' : 'en';
        setLanguage(language);
        setSelectedIds(validIds);
        // Refresh the localStorage cache so the next open paints the right value instantly.
        saveExportConfig(callerKey, { language, selectedColumns: validIds });
      })
      .catch(() => { /* network failure → keep cached values, graceful degradation */ });
    return () => { cancelled = true; };
  }, [isOpen, callerKey, fallback]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // Display order: selected columns first (in user order), then the unselected
  // ones in registry order. This keeps reordering meaningful and makes it easy
  // to add a new column to the end of the selection.
  const displayList = useMemo(() => {
    const unselected = EXPORT_COLUMNS
      .map((c) => c.id)
      .filter((id) => !selectedSet.has(id));
    return [...selectedIds, ...unselected];
  }, [selectedIds, selectedSet]);

  const toggle = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const move = (id, direction) => {
    setSelectedIds((prev) => {
      const idx = prev.indexOf(id);
      if (idx === -1) return prev;
      const target = idx + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  // Drop sourceId onto the slot occupied by targetId in the visible list.
  // - If source is selected and target is selected: reposition within selectedIds.
  // - If source is unselected: insert it into selectedIds at target's position
  //   (auto-selects on drop). If target is unselected, append to end.
  // - If source is selected but target is unselected: append to end.
  const reorderTo = (sourceId, targetId) => {
    if (!sourceId || sourceId === targetId) return;
    setSelectedIds((prev) => {
      const sourceSelected = prev.includes(sourceId);
      const withoutSource = prev.filter((x) => x !== sourceId);
      const targetIdx = withoutSource.indexOf(targetId);
      const insertAt = targetIdx === -1 ? withoutSource.length : targetIdx;
      if (!sourceSelected || prev.includes(sourceId)) {
        const next = [...withoutSource];
        next.splice(insertAt, 0, sourceId);
        return next;
      }
      return prev;
    });
  };

  const selectAll = () => setSelectedIds(EXPORT_COLUMNS.map((c) => c.id));
  const selectNone = () => setSelectedIds([]);
  const resetToDefault = () => {
    setLanguage(defaultLanguage);
    setSelectedIds(defaultSelectedColumns);
  };

  const handleExport = () => {
    if (selectedIds.length === 0) return;
    const config = { language, selectedColumns: selectedIds };
    saveExportConfig(callerKey, config);
    // Persist per-domain on the server. Fire-and-forget — don't block the export.
    fetch(`${BACKEND_URL}/api/export-config/${callerKey}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    }).catch(() => { /* localStorage already saved; silent fallback */ });
    onConfirm(config);
  };

  if (!isOpen) return null;

  const c = theme?.colors || {};
  const radius = theme?.borderRadius || 4;
  const surface = c.surface || '#fff';
  const bg = c.background || '#f5f5f5';
  const text = c.text || '#000';
  const textSecondary = c.textSecondary || '#666';
  const border = c.border || '#ccc';
  const primary = c.primary || '#1976d2';
  const textLight = c.textLight || '#fff';
  const error = c.error || '#d32f2f';

  const sectionStyle = {
    backgroundColor: bg,
    padding: '0.75rem 1rem',
    borderRadius: radius,
    marginBottom: '1rem',
  };

  const buttonStyle = (variant = 'default') => {
    if (variant === 'primary') {
      return {
        backgroundColor: primary,
        color: textLight,
        border: 'none',
        borderRadius: radius,
        padding: '0.5rem 1rem',
        cursor: 'pointer',
        fontWeight: 'bold',
      };
    }
    if (variant === 'danger') {
      return {
        backgroundColor: error,
        color: textLight,
        border: 'none',
        borderRadius: radius,
        padding: '0.5rem 1rem',
        cursor: 'pointer',
      };
    }
    return {
      backgroundColor: surface,
      color: text,
      border: `1px solid ${border}`,
      borderRadius: radius,
      padding: '0.35rem 0.75rem',
      cursor: 'pointer',
      fontSize: '0.85rem',
    };
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: surface,
          borderRadius: theme?.borderRadius || 8,
          padding: '1.25rem',
          maxWidth: '640px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          color: text,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          borderBottom: `2px solid ${primary}`,
          paddingBottom: '0.5rem',
        }}>
          <h2 style={{ color: primary, margin: 0, fontSize: '1.35rem' }}>
            {t('exportSettings')}
          </h2>
          <button onClick={onClose} style={buttonStyle('danger')}>
            {t('close')}
          </button>
        </div>

        {/* Language picker */}
        <div style={sectionStyle}>
          <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
            {t('exportLanguage')}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {['en', 'es'].map((lang) => {
              const active = language === lang;
              return (
                <button
                  key={lang}
                  onClick={() => setLanguage(lang)}
                  style={{
                    backgroundColor: active ? primary : surface,
                    color: active ? textLight : text,
                    border: `1px solid ${active ? primary : border}`,
                    borderRadius: radius,
                    padding: '0.4rem 1rem',
                    cursor: 'pointer',
                    fontWeight: active ? 'bold' : 'normal',
                  }}
                >
                  {lang === 'en' ? 'English' : 'Español'}
                </button>
              );
            })}
          </div>
        </div>

        {/* Columns picker */}
        <div style={{ ...sectionStyle, flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.5rem',
            flexWrap: 'wrap',
            gap: '0.5rem',
          }}>
            <div style={{ fontWeight: 'bold' }}>
              {t('exportColumns')}{' '}
              <span style={{ color: textSecondary, fontWeight: 'normal' }}>
                ({selectedIds.length}/{EXPORT_COLUMNS.length})
              </span>
            </div>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              <button onClick={selectAll} style={buttonStyle()}>{t('selectAll')}</button>
              <button onClick={selectNone} style={buttonStyle()}>{t('selectNone')}</button>
              <button onClick={resetToDefault} style={buttonStyle()}>{t('resetToDefault')}</button>
            </div>
          </div>

          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {displayList.map((id) => {
              const col = getColumnById(id);
              if (!col) return null;
              const isSelected = selectedSet.has(id);
              const positionInSelection = selectedIds.indexOf(id);
              const isDragging = dragId === id;
              const isDropTarget = dragOverId === id && dragId && dragId !== id;
              return (
                <li
                  key={id}
                  draggable
                  onDragStart={(e) => {
                    setDragId(id);
                    e.dataTransfer.effectAllowed = 'move';
                    // Required for Firefox to initiate drag.
                    e.dataTransfer.setData('text/plain', id);
                  }}
                  onDragOver={(e) => {
                    if (!dragId || dragId === id) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (dragOverId !== id) setDragOverId(id);
                  }}
                  onDragLeave={() => {
                    if (dragOverId === id) setDragOverId(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    reorderTo(dragId, id);
                    setDragId(null);
                    setDragOverId(null);
                  }}
                  onDragEnd={() => {
                    setDragId(null);
                    setDragOverId(null);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.4rem 0.5rem',
                    backgroundColor: isSelected ? surface : 'transparent',
                    border: `1px solid ${isDropTarget ? primary : (isSelected ? border : 'transparent')}`,
                    borderRadius: radius,
                    marginBottom: '0.25rem',
                    opacity: isDragging ? 0.4 : 1,
                    cursor: 'grab',
                  }}
                >
                  <span
                    aria-hidden="true"
                    title={t('dragToReorder')}
                    style={{
                      color: textSecondary,
                      fontSize: '1.1rem',
                      lineHeight: 1,
                      userSelect: 'none',
                      cursor: 'grab',
                    }}
                  >
                    ⋮⋮
                  </span>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(id)}
                    aria-label={translateInLanguage(col.labelKey, language)}
                  />
                  <span style={{ flex: 1, color: isSelected ? text : textSecondary }}>
                    {translateInLanguage(col.labelKey, language)}
                  </span>
                  <button
                    onClick={() => move(id, -1)}
                    disabled={!isSelected || positionInSelection <= 0}
                    style={{
                      ...buttonStyle(),
                      opacity: !isSelected || positionInSelection <= 0 ? 0.4 : 1,
                      cursor: !isSelected || positionInSelection <= 0 ? 'not-allowed' : 'pointer',
                      padding: '0.2rem 0.55rem',
                    }}
                    aria-label="Move up"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => move(id, 1)}
                    disabled={!isSelected || positionInSelection === selectedIds.length - 1}
                    style={{
                      ...buttonStyle(),
                      opacity: !isSelected || positionInSelection === selectedIds.length - 1 ? 0.4 : 1,
                      cursor: !isSelected || positionInSelection === selectedIds.length - 1 ? 'not-allowed' : 'pointer',
                      padding: '0.2rem 0.55rem',
                    }}
                    aria-label="Move down"
                  >
                    ↓
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.5rem',
          marginTop: '0.5rem',
        }}>
          <button onClick={onClose} style={buttonStyle()}>{t('cancel')}</button>
          <button
            onClick={handleExport}
            disabled={exporting || selectedIds.length === 0}
            style={{
              ...buttonStyle('primary'),
              opacity: exporting || selectedIds.length === 0 ? 0.6 : 1,
              cursor: exporting || selectedIds.length === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            {exporting ? `${t('exporting')}...` : t('export')}
          </button>
        </div>
      </div>
    </div>
  );
}
